import { RepairReasonResponseSchema } from '@/lib/service/ecs/contracts';
import type { RepairReasonResponse } from '@/lib/service/ecs/contracts';
import { devError } from '@/lib/utils/devLog';

const REPAIR_REASON_GENERIC_FAILURE = 'REPAIR_REASON_GENERIC_FAILURE';

export interface RepairReasonQuery {
  countryCode: string;
  locale: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readErrorMessage(json: unknown): string {
  if (!isRecord(json) || !Array.isArray(json.errors)) return '';
  const first = json.errors[0];
  if (isRecord(first) && typeof first.message === 'string' && first.message) return first.message;
  return '';
}

export function buildRepairReasonUrl(query: RepairReasonQuery): string {
  const params = new URLSearchParams({
    countryCode: query.countryCode,
    locale: query.locale,
  });
  return `/api/service/repair-reason?${params.toString()}`;
}

export async function fetchRepairReasons(query: RepairReasonQuery): Promise<RepairReasonResponse> {
  let response: Response;
  try {
    response = await fetch(buildRepairReasonUrl(query));
  } catch (error) {
    devError('[repairReasonClient] request failed:', error);
    throw new Error(REPAIR_REASON_GENERIC_FAILURE, { cause: error });
  }

  const json: unknown = await response.json().catch(() => null);
  if (!response.ok || !isRecord(json) || json.success !== true) {
    throw new Error(readErrorMessage(json) || REPAIR_REASON_GENERIC_FAILURE);
  }

  const parsed = RepairReasonResponseSchema.safeParse(json.data ?? {});
  if (!parsed.success) throw new Error(REPAIR_REASON_GENERIC_FAILURE);
  return parsed.data;
}
